import { getFeaturePanelDefinition } from "../feature-panel-registry.ts";
import type { RuntimeFeatureModule } from "../runtime-feature-registry.ts";
import {
  applyReadingGuideEffect,
  resetReadingGuideEffect,
  type ReadingGuideMode,
} from "../shared/page-reading-effects.ts";

const allowedModes = ["pointer", "center"] as const;

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null && !Array.isArray(value);
}

function readBooleanDefault(config: unknown) {
  if (!isRecord(config)) {
    return false;
  }

  if (typeof config.enabledByDefault === "boolean") {
    return config.enabledByDefault;
  }

  return typeof config.defaultEnabled === "boolean" ? config.defaultEnabled : false;
}

function readMode(config: unknown): ReadingGuideMode {
  const configuredMode =
    isRecord(config) && typeof config.mode === "string" ? config.mode.trim() : "pointer";

  return allowedModes.includes(configuredMode as ReadingGuideMode)
    ? (configuredMode as ReadingGuideMode)
    : "pointer";
}

function readHeight(config: unknown) {
  const configuredHeight =
    isRecord(config) && typeof config.height === "number" ? config.height : 48;

  if (!Number.isFinite(configuredHeight)) {
    return 48;
  }

  return Math.min(96, Math.max(24, Math.round(configuredHeight)));
}

function resolveEnabled(config: unknown, preference: unknown) {
  return typeof preference === "boolean" ? preference : readBooleanDefault(config);
}

function applyGuide(document: Document, config: unknown) {
  applyReadingGuideEffect({
    document,
    height: readHeight(config),
    mode: readMode(config),
  });
}

const definition = getFeaturePanelDefinition("reading-guide");

if (!definition) {
  throw new Error("Missing reading-guide feature definition");
}

export const readingGuideFeatureModule: RuntimeFeatureModule = {
  key: "reading-guide",
  definition,
  isSupported({ document }) {
    return Boolean(document.head && document.body && document.documentElement);
  },
  renderControl({ config, document, onPreferenceChange, preference }) {
    const wrapper = document.createElement("label");
    const input = document.createElement("input");
    const text = document.createElement("span");

    wrapper.className = "migrosone-a11y-widget-feature-control";
    input.type = "checkbox";
    input.checked = resolveEnabled(config, preference);
    text.textContent = "Okuma cetveli";

    input.addEventListener("change", () => {
      onPreferenceChange(input.checked);
    });

    wrapper.append(input, text);
    return wrapper;
  },
  apply({ config, document, preference }) {
    resetReadingGuideEffect({ document });
    if (!resolveEnabled(config, preference)) {
      return;
    }

    applyGuide(document, config);
  },
  reset({ document }) {
    resetReadingGuideEffect({ document });
  },
};
